import { useOnClickOutside } from "@/ui/hooks/useOnClickOutside";
import emitter from "@/core/events/eventemitter";
import { useEffect, useRef, useState } from "preact/hooks";
import clsx from "clsx";
import { Button } from "@/ui/components/Elements";
import useLocalStorage from "@/ui/hooks/useLocalStorage";
import { useArtwork } from "../../context/artwork-context";

const contentItems = [
  { id: "immersal_sparse_114026" },
  { id: "immersal_sparse_114028" },
  { id: "immersal_sparse_114031" },
  { id: "building_mesh" },
  { id: "ballet" },
  { id: "catdance" },
  { id: "taphappenings" },
  { id: "govisland" },
  { id: "annabelle" },
  { id: "idrani" },
  { id: "rooftop" },
  { id: "breakers" },
];

const DebugMenu = () => {
  const [isOpen, setIsOpen] = useState(false);
  const [checkedItems, setCheckedItems] = useState<{ [key: string]: boolean }>(
    {}
  );
  const [config, setConfig] = useState<string | null>(null);
  const [app, setApp] = useState<string | null>(null);
  const {clearArtworks} = useArtwork();

  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    setConfig(params.get("config") ?? "onsite");
    setApp(params.get("app") ?? "webxr");
  }, []);

  const dropdownRef = useRef(null);

  const toggleDropdown = () => {
    setIsOpen(!isOpen);
  };

  useOnClickOutside(dropdownRef, () => setIsOpen(false));

  const handleContentVisibilityToggle = (
    contentId: string,
    triggeredExternally = false
  ) => {
    setCheckedItems((prevState) => {
      const isChecked = !prevState[contentId];
      const newCheckedItems = { ...prevState };
      if (!contentId.startsWith("immersal") && contentId !== "building_mesh") {
        Object.keys(newCheckedItems).forEach((key) => {
          if (!key.startsWith("immersal") && key !== "building_mesh") {
            newCheckedItems[key] = false;
          }
        });
      }

      newCheckedItems[contentId] = triggeredExternally
        ? !prevState[contentId]
        : isChecked;

      if (!triggeredExternally) {
        emitter.emit(isChecked ? "content-play" : "content-unload", contentId);
      }
      setTimeout(() => {
        setIsOpen(false)
      }, 200)
      return newCheckedItems;
    });
  };

  useEffect(() => {
    const handleContentPlay = (contentId: string) => {
      setCheckedItems((prevState) => {
        const newCheckedItems = { ...prevState };

        if (
          !contentId.startsWith("immersal") &&
          contentId !== "building_mesh"
        ) {
          Object.keys(newCheckedItems).forEach((key) => {
            if (!key.startsWith("immersal") && key !== "building_mesh") {
              newCheckedItems[key] = false;
            }
          });
        }

        newCheckedItems[contentId] = true;
        return newCheckedItems;
      });
    };

    const handleContentUnload = (contentId: string) => {
      setCheckedItems((prevState) => ({
        ...prevState,
        [contentId]: false,
      }));
    };

    emitter.on("content-loading", handleContentPlay);
    emitter.on("content-unload", handleContentUnload);

    return () => {
      emitter.off("content-loading", handleContentPlay);
      emitter.off("content-unload", handleContentUnload);
    };
  }, []);

  useEffect(() => {
    const handleLoaded = () => {
      const contentIds = contentItems.map(({ id }) => ({
        id,
      }));
      setCheckedItems(
        contentIds.reduce((acc, item) => {
          if (item.id.startsWith("immersal")) {
            acc[item.id as string] = true;
          } else {
            acc[item.id as string] = false;
          }
          return acc;
        }, {} as { [key: string]: boolean })
      );
    };
    emitter.on("xr-started", handleLoaded);

    return () => {
      emitter.off("xr-started", handleLoaded);
    };
  }, []);

  const handleClearArtworks = () => { 
    clearArtworks();
    setTimeout(() => {
      setIsOpen(false)
    }, 200)
  }

  return (
    <div ref={dropdownRef} className="relative z-10">
      <Button onClick={toggleDropdown} className="xw-full h-[35px] bg-black text-xs !px-[8px] !pb-3">
        Debug &#x25BC;
      </Button>
      {isOpen && (
        <ul className="absolute w-full top-full xmb-2 text-black text-[8px] rounded-sm cursor-pointer bg-white border border-gray-900">
          {contentItems
            .filter((item) => {
              if (
                (item.id.startsWith("immersal") && config?.startsWith("offsite")) ||
                ((item.id === "building_mesh") && config?.startsWith("offsite"))) {
                return false;
              }
              return true;
            })
            .map((item, index) => (
              <li
                key={item.id}
                className={clsx(
                  "px-3 py-[10px] rounded-sm cursor-pointer hover:bg-opacity-90 flex items-center",
                  {
                    "border-b-2 border-gray-900": item.id === "building_mesh",
                  }
                )}
                onClick={() => handleContentVisibilityToggle(item.id)}
              >
                <input
                  type="checkbox"
                  checked={!!checkedItems[item.id!]}
                  onClick={(e) => e.stopPropagation()}
                  onChange={() => handleContentVisibilityToggle(item.id)}
                  className="mr-2 pointer-events-auto"
                />
                {item.id.startsWith("immersal") && `Point Cloud ${index + 1}`}
                {item.id === "building_mesh" && "Building"}
                {!item.id.startsWith("immersal") &&
                  item.id !== "building_mesh" &&
                  item.id}
              </li>
            ))}
            <button className="pointer-events-auto text-white h-[35px] w-full bg-black bg-opacity-70 text-[10px] !px-[8px] !pb-1 active:bg-opacity-20" onClick={handleClearArtworks}>Clear data</button>
        </ul>
      )}
    </div>
  );
};
export default DebugMenu;
